import { SubmitFormRequestElementResult } from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import { FormBlockerElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'

import { getCheckboxElementKey } from '../ui/FormBlocker/elements/CheckboxElement/CheckboxElement'

type FormValues = Record<string, SubmitFormRequestElementResult>

type ElementValidator = (elements: FormBlockerElement[], formValues: FormValues) => boolean

/**
 *
 * @param inputName Get the value of a nested form value.
 * @param formValues The form values.
 * @returns The value of the nested form value.
 *
 * Example:
 * getFormValue('1.check_box_result.is_checked', {
 *   '1': { 'check_box_result': { 'is_checked': true } },
 *   '2': { 'check_box_result': { 'is_checked': false } }
 * }) => true
 *
 * In the example above, we're trying to find the value of the innermost key 'is_checked' in the form values.
 */
function getFormValue(inputName: string, formValues: FormValues): unknown {
  const inputNameKeys = inputName.split('.')

  let drilledDownFormValue: unknown = formValues
  for (const key of inputNameKeys) {
    if (typeof drilledDownFormValue !== 'object' || drilledDownFormValue === null || !(key in drilledDownFormValue)) {
      return
    }

    drilledDownFormValue = (drilledDownFormValue as FormValues)[key]
  }

  return drilledDownFormValue
}

/**
 * Validators for FormElements that can belong to form input groups
 */

/**
 * CellDefaultElement validator:
 * If the cell default element has a selection group, it must have at least one checked item in the selection group.
 */
export const cellDefaultElementValidator = (elements: FormBlockerElement[], formValues: FormValues) => {
  // Get unique selection group IDs from elements
  const elementGroupIds = new Set(elements.map(el => el.cell_default_element?.selection_group_id).filter(Boolean))

  // Get unique selection group IDs from formValues
  const formValueGroupIds = new Set(
    Object.values(formValues)
      .map(item => item.cell_default_result?.selection_group_id)
      .filter(Boolean)
  )

  // Ensure every group ID from elements is present in formValues
  const allGroupsRepresented = [...elementGroupIds].every(groupId => formValueGroupIds.has(groupId))
  if (!allGroupsRepresented) {
    return false
  }

  // Check each group has at least one checked item
  return [...elementGroupIds].every(groupId =>
    Object.values(formValues).some(
      item => item.cell_default_result?.selection_group_id === groupId && item.cell_default_result?.is_checked
    )
  )
}

/**
 * CheckboxElement validator:
 * If the checkbox element is required, it must be checked OR
 * If the checkbox element has a fulfills_requirements group, it must have at least one checked item in the group.
 */
export const checkboxElementValidator = (elements: FormBlockerElement[], formValues: FormValues): boolean => {
  // Map to track each requirement name and if at least one checkbox in the group is checked
  const checkboxGroupValidationResults = new Map<string, boolean>()
  let areAllRequiredCheckboxesChecked = true
  elements.forEach((element, formSectionIndex) => {
    const checkboxElement = element.checkbox_element

    // Validate that the required checkbox is checked
    if (checkboxElement?.is_required) {
      const checkboxInputName = getCheckboxElementKey(formSectionIndex)
      const checkboxIsChecked = getFormValue(checkboxInputName, formValues) === true
      if (!checkboxIsChecked) {
        areAllRequiredCheckboxesChecked = false
      }
      return
    }

    // Validate that every checkbox group has at least one checked item
    if (checkboxElement?.fulfills_requirements && checkboxElement.fulfills_requirements.length > 0) {
      const checkboxRequirements = checkboxElement.fulfills_requirements
      const checkboxInputName = getCheckboxElementKey(formSectionIndex)
      const checkboxIsChecked = getFormValue(checkboxInputName, formValues) === true

      // Process each requirement this checkbox fulfills
      checkboxRequirements.forEach(checkboxReq => {
        const existing = checkboxGroupValidationResults.get(checkboxReq)
        if (!existing) {
          checkboxGroupValidationResults.set(checkboxReq, checkboxIsChecked)
        } else {
          if (checkboxIsChecked) {
            checkboxGroupValidationResults.set(checkboxReq, true)
          }
        }
      })
    }
    return
  })

  // check that all requirements are fulfilled
  return [...checkboxGroupValidationResults.values()].every(Boolean) && areAllRequiredCheckboxesChecked
}

/**
 * Registry of validators keyed by element type.
 * Add new validators here as new element types require input group validation.
 */
const elementValidators: Partial<Record<keyof FormBlockerElement, ElementValidator>> = {
  cell_default_element: cellDefaultElementValidator,
  checkbox_element: checkboxElementValidator,
}

export function validateInputGroupHasSelection(elements: FormBlockerElement[], formValues: FormValues): boolean {
  return Object.values(elementValidators).every(validator => validator(elements, formValues))
}
